import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import type { Bucket, CreateItem, Item, Status } from "../api/types";
import { AvatarStack, DueChip, LabelChips, PriorityMarker, SyncBadges } from "../components/bits";
import { QuickAdd } from "../components/QuickAdd";
import { MenuButton } from "../components/ContextMenu";
import { useItemMenu } from "../components/ItemMenu";
import { isClosed } from "../lib/format";
import { groupItems, type ItemGroup } from "../lib/kanban";
import { patchItem, toastError } from "../store/app";
import { useActorMap, useActors } from "../store/hooks";
import { firstStatusOf } from "./CollectionPage";

export function toggleDone(item: Item, statuses: Status[]): void {
  const reopening = isClosed(item.status_category);
  const target = reopening
    ? firstStatusOf(statuses, ["todo", "backlog", "in_progress"])
    : statuses.find((s) => s.category === "done");
  if (!target) {
    if (!reopening) toastError(new Error("This collection has no done status"));
    return;
  }
  patchItem(item.id, { status_id: target.id }, { status_category: target.category }).catch((e) =>
    toastError(e, "Could not update status"),
  );
}

function TitleEditor({ item }: { item: Item }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(item.title);
  const navigate = useNavigate();
  const location = useLocation();
  const commit = () => {
    setEditing(false);
    const t = value.trim();
    if (t && t !== item.title) {
      patchItem(item.id, { title: t }).catch((e) => toastError(e, "Rename failed"));
    } else setValue(item.title);
  };
  if (editing) {
    return (
      <input
        className="row-title-input"
        aria-label="Task title"
        value={value}
        autoFocus
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") {
            setValue(item.title);
            setEditing(false);
          }
        }}
      />
    );
  }
  return (
    <span className="row-title-wrap">
      <button
        type="button"
        className="row-title"
        title="Click to edit (open with ↗)"
        onClick={() => {
          setValue(item.title);
          setEditing(true);
        }}
      >
        {item.title || "(untitled)"}
      </button>
      <button
        type="button"
        className="icon-btn open-btn"
        aria-label={`Open ${item.title}`}
        title="Open details"
        onClick={() => navigate(`/items/${item.id}`, { state: { background: location } })}
      >
        ↗
      </button>
    </span>
  );
}

export function ItemRow({ item, statuses, showCollection }: { item: Item; statuses: Status[]; showCollection?: string }) {
  const actors = useActorMap();
  const done = isClosed(item.status_category);
  const statusName = statuses.find((s) => s.id === item.status_id)?.name;
  const menu = useItemMenu(item);
  return (
    <li className={`row${done ? " done" : ""}${menu.isOpen ? " menu-open" : ""}`} {...menu.targetProps}>
      <input
        type="checkbox"
        className="row-check"
        checked={item.status_category === "done"}
        aria-label={done ? `Reopen ${item.title}` : `Complete ${item.title}`}
        onChange={() => toggleDone(item, statuses)}
        disabled={statuses.length === 0}
      />
      <PriorityMarker priority={item.priority} />
      <TitleEditor item={item} />
      <span className="row-meta">
        {showCollection && <CollectionChip id={item.collection_id} name={showCollection} />}
        {statusName && <span className="chip chip-status">{statusName}</span>}
        <DueChip item={item} />
        <LabelChips labels={item.labels} />
        <SyncBadges sync={item.sync} />
        <AvatarStack ids={item.assignees} actors={actors} />
      </span>
      <MenuButton label={`${item.title || "(untitled)"} actions`} expanded={menu.isOpen} onOpen={menu.openFrom} className="row-more" />
      {menu.menu}
    </li>
  );
}

/** Small chip linking to a collection (space overview cards and rows, my tasks). */
export function CollectionChip({ id, name }: { id: string; name: string }) {
  return (
    <Link
      to={`/c/${id}`}
      className="chip chip-col"
      title={`Open ${name}`}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {name}
    </Link>
  );
}

/** Collapsible groups of rows (status / bucket / collection groups). */
export function GroupedRows({
  groups,
  statusesOf,
  collectionName,
}: {
  groups: ItemGroup[];
  statusesOf: (item: Item) => Status[];
  /** Show a collection chip on each row (space overview). */
  collectionName?: (item: Item) => string | undefined;
}) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  return (
    <>
      {groups.map((g) => {
        const isCollapsed = collapsed[g.key] ?? g.collapsedByDefault;
        return (
          <section key={g.key} className="group">
            <h2 className="group-head">
              <button
                type="button"
                aria-expanded={!isCollapsed}
                onClick={() => setCollapsed((c) => ({ ...c, [g.key]: !isCollapsed }))}
              >
                <span className="caret" aria-hidden="true">
                  {isCollapsed ? "▸" : "▾"}
                </span>
                {g.category && <span className={`cat-dot cat-${g.category}`} />}
                {g.title}
                <span className="count">{g.items.length}</span>
              </button>
            </h2>
            {!isCollapsed && (
              <ul className="rows">
                {g.items.map((it) => (
                  <ItemRow key={it.id} item={it} statuses={statusesOf(it)} showCollection={collectionName?.(it)} />
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </>
  );
}

/** "Group: a / b" segmented toggle. */
export function GroupToggle<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="group-toggle">
      <span className="muted small">Group:</span>
      <div className="seg" role="radiogroup" aria-label="Group by">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={value === o.value}
            className={value === o.value ? "active" : ""}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function ListView({
  statuses,
  items,
  onCreate,
  buckets,
}: {
  collectionId: string;
  statuses: Status[];
  items: Item[];
  onCreate: (body: CreateItem) => Promise<unknown>;
  labels?: string[];
  /** v0.4: set when the collection has buckets enabled (offers grouping by bucket). */
  buckets?: Bucket[];
}) {
  const actorList = useActors();
  const [mode, setMode] = useState<"status" | "bucket">("status");
  const groupMode = buckets && mode === "bucket" ? "bucket" : "status";
  const groups = useMemo(() => groupItems(items, groupMode, { buckets }), [items, groupMode, buckets]);
  const defaultStatus = firstStatusOf(statuses, ["todo", "backlog", "in_progress"]);

  return (
    <div className="list-view">
      <QuickAdd
        statuses={statuses}
        buckets={buckets}
        actors={actorList}
        defaultStatusId={defaultStatus?.id}
        onCreate={onCreate}
      />
      {buckets && (
        <GroupToggle
          value={groupMode}
          options={[
            { value: "status", label: "Status" },
            { value: "bucket", label: "Bucket" },
          ]}
          onChange={setMode}
        />
      )}
      {/* Remount on mode change so each grouping starts with its own default collapse state. */}
      <GroupedRows key={groupMode} groups={groups} statusesOf={() => statuses} />
      {items.length === 0 && <p className="muted pad">Nothing here yet. Add a task above.</p>}
    </div>
  );
}
